/**
 * Email Service
 * Handles sending emails through the backend API
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_KEY = process.env.REACT_APP_API_KEY;

/**
 * Get display name for form source
 */
const getSourceDisplay = (source) => {
    const sources = {
        'home': 'Homepage',
        'contact': 'Contact Page'
    };
    return sources[source] || 'Website';
};

/**
 * Generate professional HTML email template for contact forms
 * Designed to be read by both the business and the client (who is CC'd)
 */
const generateContactEmailTemplate = (formData, source = 'website') => {
    const { name, email, topic, category, subject, message } = formData;
    const timestamp = new Date().toLocaleString('en-US', {
        dateStyle: 'full',
        timeStyle: 'short',
        timeZone: 'Europe/Paris'
    });

    // Use category or topic for the inquiry type
    const inquiryType = category || topic || 'General Inquiry';
    const sourceDisplay = getSourceDisplay(source);

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>New Contact Request - RiskDiscover</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
    <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 40px 20px;">
        <tr>
            <td align="center">
                <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 12px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                    <!-- Header -->
                    <tr>
                        <td style="background: linear-gradient(135deg, #2563eb 0%, #4f46e5 100%); padding: 40px 40px 30px; border-radius: 12px 12px 0 0; text-align: center;">
                            <h1 style="margin: 0; color: #ffffff; font-size: 28px; font-weight: 700; letter-spacing: -0.5px;">
                                New Contact Request
                            </h1>
                            <p style="margin: 10px 0 0; color: #e0e7ff; font-size: 14px;">
                                Received from ${sourceDisplay} • ${timestamp}
                            </p>
                        </td>
                    </tr>

                    <!-- Confirmation Message -->
                    <tr>
                        <td style="padding: 30px 40px 20px;">
                            <div style="background-color: #eff6ff; border-left: 4px solid #2563eb; padding: 16px; border-radius: 6px; margin-bottom: 30px;">
                                <p style="margin: 0; color: #1e40af; font-size: 14px; line-height: 1.6;">
                                    <strong>Thank you for reaching out!</strong><br>
                                    We've received your message and our team will get back to you within 24 hours.
                                </p>
                            </div>
                        </td>
                    </tr>

                    <!-- Contact Information -->
                    <tr>
                        <td style="padding: 0 40px 30px;">
                            <table width="100%" cellpadding="0" cellspacing="0">
                                <tr>
                                    <td style="padding-bottom: 20px;">
                                        <h2 style="margin: 0 0 20px; color: #1f2937; font-size: 18px; font-weight: 600; border-bottom: 2px solid #e5e7eb; padding-bottom: 10px;">
                                            Contact Details
                                        </h2>
                                    </td>
                                </tr>
                                <tr>
                                    <td style="padding: 12px 0;">
                                        <table width="100%" cellpadding="0" cellspacing="0">
                                            <tr>
                                                <td width="120" style="color: #6b7280; font-size: 14px; font-weight: 600; vertical-align: top;">Name:</td>
                                                <td style="color: #1f2937; font-size: 14px;">${name}</td>
                                            </tr>
                                        </table>
                                    </td>
                                </tr>
                                <tr>
                                    <td style="padding: 12px 0;">
                                        <table width="100%" cellpadding="0" cellspacing="0">
                                            <tr>
                                                <td width="120" style="color: #6b7280; font-size: 14px; font-weight: 600; vertical-align: top;">Email:</td>
                                                <td style="color: #2563eb; font-size: 14px;">
                                                    <a href="mailto:${email}" style="color: #2563eb; text-decoration: none;">${email}</a>
                                                </td>
                                            </tr>
                                        </table>
                                    </td>
                                </tr>
                                <tr>
                                    <td style="padding: 12px 0;">
                                        <table width="100%" cellpadding="0" cellspacing="0">
                                            <tr>
                                                <td width="120" style="color: #6b7280; font-size: 14px; font-weight: 600; vertical-align: top;">${category ? 'Category:' : 'Topic:'}</td>
                                                <td style="color: #1f2937; font-size: 14px;">
                                                    <span style="background-color: #dbeafe; color: #1e40af; padding: 4px 12px; border-radius: 12px; font-size: 13px; font-weight: 500;">
                                                        ${inquiryType}
                                                    </span>
                                                </td>
                                            </tr>
                                        </table>
                                    </td>
                                </tr>
                                ${subject ? `
                                <tr>
                                    <td style="padding: 12px 0;">
                                        <table width="100%" cellpadding="0" cellspacing="0">
                                            <tr>
                                                <td width="120" style="color: #6b7280; font-size: 14px; font-weight: 600; vertical-align: top;">Subject:</td>
                                                <td style="color: #1f2937; font-size: 14px; font-weight: 600;">${subject}</td>
                                            </tr>
                                        </table>
                                    </td>
                                </tr>
                                ` : ''}
                            </table>
                        </td>
                    </tr>

                    <!-- Message Content -->
                    <tr>
                        <td style="padding: 0 40px 30px;">
                            <h2 style="margin: 0 0 15px; color: #1f2937; font-size: 18px; font-weight: 600; border-bottom: 2px solid #e5e7eb; padding-bottom: 10px;">
                                Message
                            </h2>
                            <div style="background-color: #f9fafb; padding: 20px; border-radius: 8px; border: 1px solid #e5e7eb;">
                                <p style="margin: 0; color: #374151; font-size: 14px; line-height: 1.7; white-space: pre-wrap;">${message}</p>
                            </div>
                        </td>
                    </tr>

                    <!-- Next Steps (for business context) -->
                    <tr>
                        <td style="padding: 0 40px 30px;">
                            <div style="background-color: #fefce8; border-left: 4px solid #eab308; padding: 16px; border-radius: 6px;">
                                <p style="margin: 0; color: #854d0e; font-size: 13px; line-height: 1.6;">
                                    <strong>Next Steps:</strong> A member of our team will review this inquiry and respond directly to ${email} within 24 hours.
                                </p>
                            </div>
                        </td>
                    </tr>

                    <!-- Footer -->
                    <tr>
                        <td style="padding: 30px 40px; background-color: #f9fafb; border-radius: 0 0 12px 12px; border-top: 1px solid #e5e7eb;">
                            <table width="100%" cellpadding="0" cellspacing="0">
                                <tr>
                                    <td align="center">
                                        <p style="margin: 0 0 15px; color: #6b7280; font-size: 13px;">
                                            This message was sent via the RiskDiscover ${sourceDisplay.toLowerCase()}
                                        </p>
                                        <p style="margin: 0; color: #9ca3af; font-size: 12px;">
                                            <strong>RiskDiscover</strong> | AI-Powered Risk Assessment<br>
                                            <a href="https://riskdiscover.com" style="color: #2563eb; text-decoration: none;">riskdiscover.com</a>
                                        </p>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                </table>

                <!-- Additional Footer -->
                <table width="600" cellpadding="0" cellspacing="0" style="margin-top: 20px;">
                    <tr>
                        <td align="center" style="padding: 20px;">
                            <p style="margin: 0; color: #9ca3af; font-size: 11px; line-height: 1.6;">
                                You are receiving this email because a contact request was submitted on RiskDiscover.<br>
                                To respond to this inquiry, simply reply to this email.
                            </p>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>
    `.trim();
};

/**
 * Send contact form email via backend API
 * @param {Object} formData - Contact form data (name, email, message, and topic/category/subject)
 * @param {string} source - Form source: 'home' or 'contact'
 * @param {string} recipientEmail - Recipient email address (info@ or support@)
 * @returns {Promise<Object>} API response
 */
export const sendContactEmail = async (formData, source = 'website', recipientEmail = 'info@riskdiscover.com') => {
    if (!API_KEY) {
        throw new Error('Email API key not configured. Please contact the administrator.');
    }

    const htmlContent = generateContactEmailTemplate(formData, source);

    // Generate appropriate subject line
    const inquiryType = formData.category || formData.topic || 'General Inquiry';
    const subjectPrefix = formData.subject ? formData.subject : inquiryType;
    const subject = `New Contact Request: ${subjectPrefix}`;

    const emailPayload = {
        to: [recipientEmail],
        cc: [formData.email], // Client is CC'd
        subject: subject,
        html: htmlContent,
        reply_to: formData.email
    };

    try {
        const response = await fetch(`${API_BASE_URL}/api/emails`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': API_KEY
            },
            body: JSON.stringify(emailPayload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `Failed to send email: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error sending contact email:', error);
        throw error;
    }
};

const emailService = {
    sendContactEmail
};

export default emailService;
